import React, { useCallback, useEffect, useRef, useState } from "react";
import NextLink from "next/link";
import { type z } from "zod";
import {
  Heading,
  Button,
  Box,
  Collapse,
  ErrorText,
  Step,
  Select,
  TextField,
  Loader,
  Text,
  Link,
} from "@cruk/cruk-react-components";
import { useForm, type SubmitHandler, useWatch } from "react-hook-form";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useRouter } from "next/compat/router";

import { createNewFundraiserUrl } from "@fwa/src/services/fundraiserService";
import { fetcher } from "@fwa/src/services/apiClient";
import { createUser, logIn } from "@fwa/src/services/auth";
import { useBrandContext } from "@fwa/src/contexts/BrandContext";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import {
  sanitiseIfUkPostCode,
  splitMobileAndLandlineNumbers,
} from "@fwa/src/utils/formUtils";
import { isBrowser } from "@fwa/src/utils/browserUtils";
import {
  getFwsEntitiesFromQueryParams,
  defaultSteps,
  updateStepsFromFundraisingPageType,
} from "@fwa/src/utils/createPageUtils";
import { fundraiserValidation } from "@fwa/src/validation/fundraiser";

import useIsTabActive from "@fwa/src/hooks/useIsTabActive";

import { AddressFields } from "@fwa/src/components/AddressFields";
import { SuppressionFields } from "@fwa/src/components/SuppressionFields";
import { useTracking } from "@fwa/src/hooks/useTracking";

import sharedStyles from "@fwa/src/components/styles.module.css";

import { type StepsType, type FundraiserType } from "@fwa/src/types";

export type SignUpValues = z.infer<typeof fundraiserValidation>;

type TitlesType = z.infer<typeof fundraiserValidation>["title"];
const defaultTitles: TitlesType[] = ["Mr", "Mrs", "Miss", "Ms", "Mx", "Other"];
const initialValues: SignUpValues = {
  title: "",
  titleOther: undefined,
  forename: "",
  surname: "",
  address: {
    country: "GBR",
    line1: "",
    line2: "",
    line3: "",
    city: "",
    postalCode: "",
    validated: false,
  },
  phoneNumber: "",
  suppressions: {
    email: "PUEM",
    phone: "LIPH",
    post: "LIPO",
    text: "PUSM",
  },
};

export const SignUpPage = () => {
  const { trackError } = useTracking();
  const [fundraiserState, setFundraiserState] = useFundraiserContext();
  const [brandName, setBrandName] = useBrandContext();
  const router = useRouter();

  const [steps, setSteps] = useState<StepsType[]>([]);
  const displaySteps = steps.filter((item) => item.includeInTracker);
  const [isLoading, setIsLoading] = useState(true);
  const hasGotDataFromURL = useRef(false);
  const [submitionErrorMessage, setSubmitionErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [hasBeenHidden, setHasBeenHidden] = useState(false);
  const [showRefreshButton, setShowRefreshButton] = useState(false);
  const isTabVisible = useIsTabActive();
  const formProps = useForm<SignUpValues>({
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: initialValues,
    criteriaMode: "firstError",
    shouldFocusError: true,
    resolver: standardSchemaResolver(fundraiserValidation),
    resetOptions: {
      keepDirtyValues: true, // user-interacted input will be retained
      keepErrors: false, // input errors will be retained with value update
    },
  });

  const {
    register,
    formState: { errors },
    control,
    handleSubmit,
  } = formProps;

  const watchTitle = useWatch({ control, name: "title" });
  const watchSuppressions = useWatch({ control, name: "suppressions" });

  // we need to get the pagetype from the query params so we know the correct steps and brand to show
  const getDataFromURL = useCallback(async () => {
    if (!isBrowser || steps.length) return;
    const { fundraisingPageTypeData } = await getFwsEntitiesFromQueryParams();
    const nextSteps = fundraisingPageTypeData
      ? updateStepsFromFundraisingPageType({
          fundraisingPageType: fundraisingPageTypeData,
          newUser: true,
          isSignUpPage: true,
        })
      : defaultSteps;
    setSteps(nextSteps);

    const foundBrandName = fundraisingPageTypeData?.brand.name || "cruk";
    if (brandName !== foundBrandName) setBrandName(foundBrandName);

    setIsLoading(false);
  }, [brandName, setBrandName, steps.length]);

  const onSubmit = handleSubmit((data) => {
    submit(data);
  });

  const submit: SubmitHandler<SignUpValues> = async (
    values,
    e,
  ): Promise<void> => {
    if (e) e.preventDefault();
    setSubmitionErrorMessage("");
    setSubmitting(true);

    const { telephoneNumberMobile, telephoneNumberLandline } =
      splitMobileAndLandlineNumbers(values.phoneNumber);

    const fundraiserData: Partial<FundraiserType> = {
      title: values.title === "Other" ? values.titleOther : values.title,
      forename: values.forename,
      surname: values.surname,
      address: {
        ...values.address,
        country: values.address.country || "GBR",
        postalCode: sanitiseIfUkPostCode(values.address.postalCode),
      },
      suppressions: Object.values(values.suppressions).filter(
        (value: string) => value.length,
      ),
      telephoneNumberMobile,
      telephoneNumberLandline,
    };

    const fundraiser: FundraiserType | void = await fetcher(
      createNewFundraiserUrl,
      {
        method: "POST",
        body: JSON.stringify(fundraiserData),
      },
    )
      .then((res) => res as FundraiserType)
      .catch((err) => {
        trackError(err as Error, {
          component: "SignUpPage",
        });
        setSubmitting(false);
        setSubmitionErrorMessage("Sorry something went wrong please try again");
      });

    if (fundraiser) {
      router
        ?.push(
          {
            pathname: router.pathname,
            query: { ...router.query, newUser: true },
          },
          undefined,
          { shallow: true },
        )
        .then(() => {
          setFundraiserState({
            ...fundraiserState,
            fundraiser,
            loggedInStatus: "loggedIn",
          });
          return undefined;
        })
        .catch((err) => {
          trackError(err as Error, {
            component: "SignUpPage",
            function: "setFundraiserState",
          });
        });

      // we don't need to wait for fundraiser context to propagate changes before push user createpage
      router
        ?.push({
          pathname: "/createpage",
          query: { ...router.query, newUser: true },
        })
        .catch((err) => {
          trackError(err as Error, {
            component: "SignUpPage",
            function: "loggedInRouterPush",
          });
        });
    }
    return undefined;
  };

  useEffect(() => {
    if (
      !router?.isReady ||
      !fundraiserState ||
      fundraiserState.loggedInStatus === "unknown"
    )
      return;
    const { create } = router.query;
    // If logged out redirect to oauth to login or create new user.
    if (fundraiserState.loggedInStatus === "loggedOut") {
      if (create) {
        createUser();
      } else {
        logIn();
      }
    }
    // If logged in with fundraiser redirect to createPage.
    if (
      fundraiserState.loggedInStatus === "loggedIn" &&
      fundraiserState.fundraiser
    ) {
      router
        ?.push(
          { pathname: "/createpage", query: { ...router.query } },
          undefined,
          {
            shallow: true,
          },
        )
        .catch((err) => {
          trackError(err as Error, {
            component: "SignUpPage",
            function: "loggedOutRouterPush",
          });
        });
    }
  }, [fundraiserState, fundraiserState.loggedInStatus, router, trackError]);

  // after render grab data from url to set the pageType, steps and theme
  // if we already have steps we don't to run this again
  useEffect(() => {
    if (!isLoading || steps.length || hasGotDataFromURL.current) return;
    hasGotDataFromURL.current = true;
    getDataFromURL().catch((err) => {
      trackError(err as Error, {
        component: "SignUpPage",
        function: "getDataFromURL",
      });
    });
  }, [getDataFromURL, isLoading, steps.length, trackError]);

  useEffect(() => {
    if (!isTabVisible) {
      setHasBeenHidden(true);
    }
    if (isTabVisible && hasBeenHidden) {
      setShowRefreshButton(true);
    }
  }, [hasBeenHidden, isTabVisible]);

  return (
    <Box
      backgroundColor="background-light"
      paddingVertical="xxl"
      paddingHorizontal="s"
      marginTop="s"
    >
      {fundraiserState.fundraiser !== null ||
      fundraiserState.loggedInStatus !== "loggedIn" ||
      isLoading ? (
        <span>
          {showRefreshButton ? (
            <div className={sharedStyles["column-align-center"]}>
              <Text>{`Sorry this page didn't load`} </Text>
              <NextLink href="/" data-cta-type="reload-on-error">
                <Button>Reload this page</Button>
              </NextLink>
            </div>
          ) : (
            <Loader />
          )}
        </span>
      ) : (
        <div
          className={sharedStyles["form-wrapper"]}
          data-component="sign-up-form"
        >
          <Heading h1>Create your Giving Page</Heading>
          <Step
            current={1}
            steps={displaySteps.map((item) => item.stepTitle)}
          />
          <Heading h2>
            Create your Cancer Research UK Giving Pages account
          </Heading>
          <form
            noValidate
            onSubmit={(e) => {
              onSubmit(e).catch((err) => {
                trackError(err as Error, { component: "SignUpPageFrom" });
              });
            }}
            method="dialog"
          >
            <div
              className={sharedStyles["field-wrapper"]}
              data-input-width="medium"
            >
              <Select {...register("title")} label="Title">
                <option value="">Please Select</option>
                {defaultTitles.map((title) => {
                  if (typeof title !== "string") return undefined;
                  return (
                    <option key={`title-${title}`} value={title}>
                      {title}
                    </option>
                  );
                })}
              </Select>
            </div>
            {watchTitle === "Other" ? (
              <div
                className={sharedStyles["field-wrapper"]}
                data-input-width="medium"
              >
                <TextField
                  {...register("titleOther")}
                  label="Please tell us your title"
                  required
                  errorMessage={errors.titleOther?.message}
                />
              </div>
            ) : null}
            <div
              className={sharedStyles["field-wrapper"]}
              data-input-width="medium"
            >
              <TextField
                {...register("forename")}
                label="First name"
                required
                errorMessage={errors.forename?.message}
              />
            </div>
            <div
              className={sharedStyles["field-wrapper"]}
              data-input-width="medium"
            >
              <TextField
                {...register("surname")}
                label="Last name"
                required
                errorMessage={errors.surname?.message}
              />
            </div>
            <AddressFields<SignUpValues> {...formProps} />

            <div className={sharedStyles["field-wrapper"]}>
              <Box marginBottom="s">
                <TextField
                  {...register("phoneNumber")}
                  label="Phone number"
                  required
                  type="tel"
                  errorMessage={errors.phoneNumber?.message}
                />
              </Box>

              <Collapse
                headerTitleText="Why are we asking for this?"
                id="phone-gdpr-explanation"
              >
                <Text>
                  {`We may use this information to identify if you’ve
                      supported Cancer Research UK before. This will help us to
                      ensure that our records are up to date so that you can
                      receive appropriate communications from us.`}
                </Text>
              </Collapse>
            </div>

            <SuppressionFields<SignUpValues>
              {...formProps}
              suppressions={watchSuppressions}
            />

            <div
              className={[sharedStyles["top-border"], "spacing-props"].join(
                " ",
              )}
              data-margin-bottom="l"
              data-padding-top="s"
            >
              <Heading h2>Personal Declaration</Heading>
              <Text marginBottom="none">
                {`By signing up, I confirm that I agree to Cancer Research UK's`}
              </Text>
              <Text>
                <Link
                  href="https://www.cancerresearchuk.org/terms-and-conditions/online-fundraising-terms-and-conditions"
                  target="_blank"
                  data-cta-type="link-terms-and-conditions"
                >
                  Giving Page Terms and Conditions.
                </Link>
              </Text>
            </div>
            <Button
              type="submit"
              appearance="primary"
              disabled={submitting}
              data-cta-type="form-continue"
            >
              Continue
            </Button>
            <Box marginTop="s">
              {submitionErrorMessage.length ? (
                <ErrorText>{submitionErrorMessage}</ErrorText>
              ) : null}
            </Box>
          </form>
        </div>
      )}
    </Box>
  );
};

export default SignUpPage;
